import {
  ScopeSectionRuleModeName,
  ScopeSectionRuleRequest,
  ScopeSectionRuleTypeName,
  ScopeResponse,
} from '@ydistri/api-sdk';

export interface ScopeSectionRuleDraft {
  id: string;
  type: ScopeSectionRuleTypeName;
  mode: ScopeSectionRuleModeName;
  valueIds: number[];
}

export interface ScopeSectionDraft {
  id: string;
  rules: ScopeSectionRuleDraft[];
}

export interface ScopeDraft {
  name: string;
  sections: ScopeSectionDraft[];
}

export interface ScopeDraftValidationIssue {
  message: string;
  ruleId?: string;
  sectionId?: string;
}

export interface ScopeRuleDefinition {
  label: string;
  value: ScopeSectionRuleTypeName;
}

const newId = (prefix: string): string => `${prefix}-${crypto.randomUUID()}`;

export const createEmptySection = (): ScopeSectionDraft => ({
  id: newId('section'),
  rules: [],
});

export const createRule = (
  type: ScopeSectionRuleTypeName,
  mode = ScopeSectionRuleModeName.Include,
): ScopeSectionRuleDraft => ({
  id: newId('rule'),
  type,
  mode,
  valueIds: [],
});

export const normalizeScopeRuleIds = (ids: number[]): number[] =>
  [...new Set(ids)].sort((left, right) => left - right);

export const toScopeDraft = (selection: ScopeResponse): ScopeDraft => ({
  name: selection.name,
  sections: selection.sections.map(section => ({
    id: section.scopeSectionId.toString(),
    rules: section.rules.map(rule => ({
      id: rule.scopeSectionRuleId.toString(),
      type: rule.scopeSectionRuleTypeId,
      mode: rule.scopeSectionRuleModeId,
      valueIds: normalizeScopeRuleIds(rule.ids),
    })),
  })),
});

export const toInitialScopeDraft = (selection: ScopeResponse): ScopeDraft => {
  const draft = toScopeDraft(selection);

  return draft.sections.length > 0 ? draft : { ...draft, sections: [createEmptySection()] };
};

export const toScopeSectionRuleRequest = (
  rule: ScopeSectionRuleDraft,
): ScopeSectionRuleRequest => ({
  scopeSectionRuleTypeId: rule.type,
  scopeSectionRuleModeId: rule.mode,
  ids: normalizeScopeRuleIds(rule.valueIds),
});

export const areScopeRuleRequestsEqual = (
  left: ScopeSectionRuleRequest,
  right: ScopeSectionRuleRequest,
): boolean =>
  left.scopeSectionRuleTypeId === right.scopeSectionRuleTypeId &&
  left.scopeSectionRuleModeId === right.scopeSectionRuleModeId &&
  JSON.stringify(normalizeScopeRuleIds(left.ids)) ===
    JSON.stringify(normalizeScopeRuleIds(right.ids));

export const getDraftValidationIssues = (draft: ScopeDraft): ScopeDraftValidationIssue[] => {
  const issues: ScopeDraftValidationIssue[] = [];

  if (!draft.name.trim()) {
    issues.push({ message: 'Name is required.' });
  }

  draft.sections.forEach((section, sectionIndex) => {
    if (section.rules.length === 0) {
      issues.push({
        message: `Section ${sectionIndex + 1} needs at least one rule.`,
        sectionId: section.id,
      });
      return;
    }

    if (!section.rules.some(rule => rule.mode === ScopeSectionRuleModeName.Include)) {
      issues.push({
        message: `Section ${sectionIndex + 1} needs at least one include rule.`,
        sectionId: section.id,
      });
    }

    section.rules.forEach(rule => {
      if (rule.valueIds.length === 0) {
        issues.push({
          message: `Select at least one value for ${rule.mode.toLowerCase()} ${rule.type} in section ${sectionIndex + 1}.`,
          ruleId: rule.id,
          sectionId: section.id,
        });
      }
    });
  });

  return issues;
};
